import React from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  RadioGroup, 
  FormControlLabel, 
  Radio, 
  Chip, 
  Autocomplete, 
  TextField,
  Divider,
  useTheme,
  Button,
  Switch,
  FormGroup
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ToggleButton from '../common/ToggleButton';

const TripFilter = ({
  // Cart Filter Props
  cartFilterType,
  setCartFilterType,
  selectedCartTypes,
  handleCartTypesChange,
  selectedCarts,
  handleSpecificCartsChange,
  cartsList,
  cartTypeOptions,
  
  // Trip Filter Props
  selectedTripStatuses,
  handleTripStatusesChange,
  tripType,
  handleTripTypeChange,
  tripStatusOptions,
  tripTypeOptions,
  
  // Y-Axis Percentage
  tripYAxisPercentage,
  handleTripYAxisPercentageChange,
  
  onFilterChange
}) => {
  const theme = useTheme();
  
  const handleCartFilterTypeChange = (e) => {
    setCartFilterType(e.target.value);
    // Remove automatic data fetch - only update local state
  };

  const handleTripTypeSelectChange = (e) => {
    handleTripTypeChange(e.target.value);
    // Remove automatic data fetch - only update local state
  };

  return (
    <Box>
      {/* Filter Content */}
      <Box sx={{ 
        p: 3, 
        background: 'transparent'
      }}>
        {/* Cart Filters Section */}
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
          Cart Selection
        </Typography>
        
        <Grid container spacing={3}>
          {/* Cart Filter Type Selection */}
          <Grid item xs={12}>
            <FormControl>
              <RadioGroup
                row
                value={cartFilterType}
                onChange={handleCartFilterTypeChange}
                name="trip-cart-filter-type"
              >
                <FormControlLabel 
                  value="all" 
                  control={<Radio size="small" />} 
                  label="All Carts by Type" 
                />
                <FormControlLabel 
                  value="specific" 
                  control={<Radio size="small" />} 
                  label="Specific Carts" 
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          {/* All Carts Filter */}
          {cartFilterType === 'all' && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Cart Types</InputLabel>
                <Select
                  multiple
                  value={selectedCartTypes}
                  onChange={(e) => handleCartTypesChange(e.target.value)}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const option = cartTypeOptions.find(opt => opt.value === value);
                        return (
                          <Chip 
                            key={value} 
                            label={option?.label || value} 
                            size="small" 
                            variant="outlined"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {cartTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Specific Carts Filter */}
          {cartFilterType === 'specific' && (
            <Grid item xs={12}>
              <Autocomplete
                multiple
                size="small"
                value={selectedCarts}
                onChange={(event, newValue) => {
                  if (newValue.length <= 3) {
                    handleSpecificCartsChange(newValue);
                  }
                }}
                options={cartsList}
                getOptionLabel={(option) => `${option.name} (${option.isActive ? 'Active' : 'Inactive'})`}
                filterSelectedOptions
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Specific Carts (max 3)"
                    placeholder="Choose carts..."
                    helperText={`${selectedCarts.length}/3 carts selected`}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={`${option.name}(${option.isActive ? 'Active' : 'Inactive'})`}
                      size="small"
                      {...getTagProps({ index })}
                      key={option.id}
                    />
                  ))
                }
                sx={{ width: '100%' }}
              />
            </Grid>
          )}
        </Grid>

        {/* Divider */}
        <Divider sx={{ my: 3 }} />

        {/* Trip Filters Section */}
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
          Trip Configuration
        </Typography>
        
        <Grid container spacing={3}>
          {/* Trip Status Filter (Multi-select) */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Trip Status</InputLabel>
              <Select
                multiple
                value={selectedTripStatuses}
                onChange={(e) => {
                  handleTripStatusesChange(e.target.value);
                  // Remove automatic data fetch - only update local state
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const option = tripStatusOptions.find(opt => opt.value === value);
                      return (
                        <Chip 
                          key={value} 
                          label={option?.label || value} 
                          size="small" 
                          variant="outlined"
                          color={
                            value === 'started' ? 'info' :
                            value === 'finished_success' ? 'success' :
                            value === 'canceled' ? 'error' : 'default'
                          }
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {tripStatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 
                            option.value === 'started' ? 'info.main' :
                            option.value === 'finished_success' ? 'success.main' :
                            option.value === 'canceled' ? 'error.main' : 'grey.400'
                        }}
                      />
                      {option.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Trip Type Filter (Single-select) */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Trip Type</InputLabel>
              <Select
                value={tripType}
                onChange={handleTripTypeSelectChange}
                label="Trip Type"
              >
                {tripTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 
                            option.value === 'single_path' ? 'primary.main' :
                            option.value === 'full_trip' ? 'secondary.main' : 'grey.400'
                        }}
                      />
                      {option.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Filter Summary */}
        <Box sx={{ mt: 3, p: 1.5, backgroundColor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Active Filters:</strong> {' '}
            {cartFilterType === 'all' 
              ? `${selectedCartTypes.length} cart type(s)` 
              : `${selectedCarts.length} specific cart(s)`}, {' '}
            {selectedTripStatuses.length} trip status(es), {' '}
            {tripType ? tripTypeOptions.find(opt => opt.value === tripType)?.label : 'All trip types'}
          </Typography>
        </Box>
        {/* Y-Axis Percentage Toggle */}
        <Box sx={{ mt: 3 }}>
          <ToggleButton
            checked={tripYAxisPercentage}
            onChange={handleTripYAxisPercentageChange}
            label="Show as Percentage - out of: all carts total trips and selected path type"
            size="medium"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default TripFilter;
